// Single source of truth for the section-based permission model.
//
// Each section groups a set of routes that members can be granted (or
// denied) access to together. Admin / super_admin bypass this entirely
// (their `option` is null on the row) — anyone with a JSON array of
// section entries on `option` is a "member" and is gated section-by-
// section.
//
// Per-route checks elsewhere in the app should NOT hardcode positional
// indexes into `option` — use canSee(option, routeKey) so a future
// reshuffle of sections doesn't silently break permissions.

// Per-route labels surfaced in the Invite / Edit dialogs so the inviter
// sees a recognisable name on each sub-toggle instead of a slug.
export const ROUTE_LABELS = {
  myapps: "All Workspaces",
  allusers: "All Users",
  renewal_reminders: "Renewal Reminders",
  warm_leads: "Warm Leads",
  inbound_funnel: "Inbound Funnel",
  revenue: "Revenue Tracker",
  compare_pages: "Compare Pages",
  email_contact: "Email Contact Us",
  contact: "Contact Us",
  adcontact: "Ad Contact Us",
  ref: "Ref Count",
  source: "Source Count",
  order_approval: "Order Approvals",
  ingram_failed_orders: "Failed Ingram Orders",
  ingram_mapping: "Ingram Mapping",
  marketplace: "Marketplace",
  adminproducts: "Admin Products",
  productapproval: "Product Approvals",
  catalog_proposals: "Catalogue Requests",
  catalog_duplicates: "Duplicate Shelves",
  vendors: "Vendors",
  website_apps: "Website Apps",
  taxonomy: "Taxonomy",
  newsletters: "Newsletter",
  "monthly-digests": "Monthly Digest",
  "email-analytics": "Email Analytics",
  "blog-studio": "Blog Studio",
  zoho_billing: "Zoho Billing",
  domain_extractor: "Domain Extractor",
};

export const SECTIONS = [
  {
    key: "user_management",
    title: "User Management",
    routes: ["myapps", "allusers"],
    landing: "myapps",
  },
  {
    key: "growth_tools",
    title: "Growth Tools",
    routes: [
      "renewal_reminders",
      "warm_leads",
      "inbound_funnel",
      "revenue",
      "compare_pages",
    ],
    landing: "warm_leads",
  },
  {
    key: "inbound_contacts",
    title: "Inbound Contacts",
    routes: ["email_contact", "contact", "adcontact", "ref", "source"],
    landing: "email_contact",
  },
  {
    key: "catalog_approvals",
    // Renamed: this is the marketplace, and it now includes the supplier side.
    title: "Marketplace",
    routes: [
      "productapproval",
      "catalog_proposals",
      "catalog_duplicates",
      "vendors",
      "marketplace",
      "adminproducts",
      "order_approval",
      "ingram_failed_orders",
      "ingram_mapping",
      "website_apps",
      "taxonomy",
    ],
    landing: "productapproval",
  },
  {
    key: "email_marketing",
    title: "Email Marketing",
    routes: ["newsletters", "monthly-digests", "email-analytics"],
    landing: "newsletters",
  },
  {
    key: "content_publishing",
    title: "Content & Publishing",
    routes: ["blog-studio"],
    landing: "blog-studio",
  },
  {
    // Posts real bills / credits / payments into Zoho Books. New section, so
    // existing members have no entry for it and stay denied until granted.
    key: "finance",
    title: "Finance",
    routes: ["zoho_billing"],
    landing: "zoho_billing",
  },
  {
    // CSV of companies -> mail provider (MX) and website host (WHOIS). New
    // section, so existing members stay denied until granted.
    key: "domain_extractor",
    title: "Domain Extractor",
    routes: ["domain_extractor"],
    landing: "domain_extractor",
  },
];

// Legacy invites used these per-route keys directly in `option[i].name`.
// If we encounter an old-shape option we project it onto sections so
// previously-invited members keep working until they're re-invited.
const LEGACY_ROUTE_KEY_MAP = {
  allworkspace: "myapps",
  alluser: "allusers",
  emailcontact: "email_contact",
  contactus: "contact",
  adcontactus: "adcontact",
  refcount: "ref",
  sourcecount: "source",
  marketplace: "marketplace",
};

function isOptionGated(option) {
  // null / undefined / empty object → admin (gate disabled)
  // array of entries → member (gate enabled)
  return Array.isArray(option) && option.length > 0;
}

// True if the current user is an admin / super_admin — i.e. NOT a
// gated member. Used by pages that must stay admin-only regardless of
// any section permission a member might hold (e.g. Admin Products,
// Product Approvals).
export function isAdmin(option) {
  return !isOptionGated(option);
}

function findSectionByRoute(routeKey) {
  return SECTIONS.find((s) => s.routes.includes(routeKey));
}

// True if `option` grants access to the given section key.
// Admin/super_admin (no option) → always true.
export function hasSection(option, sectionKey) {
  if (!isOptionGated(option)) return true;
  // New shape: entries with { key, role }
  const newEntry = option.find((o) => o?.key === sectionKey);
  if (newEntry) return newEntry.role === "allow";
  // Legacy shape: entries with { name: <route_key>, role }. We treat the
  // section as allowed iff ANY route in it is allowed under the legacy
  // map — old invites granted per-route, so being granted any sub-item
  // is enough to see the group.
  const section = SECTIONS.find((s) => s.key === sectionKey);
  if (!section) return false;
  for (const legacyKey of Object.keys(LEGACY_ROUTE_KEY_MAP)) {
    const route = LEGACY_ROUTE_KEY_MAP[legacyKey];
    if (!section.routes.includes(route)) continue;
    const e = option.find((o) => o?.name === legacyKey);
    if (e && e.role === "allow") return true;
  }
  return false;
}

// True if `option` grants access to a specific route (e.g. "warm_leads").
// Routes not belonging to any section (e.g. "invite", "tracking/:id")
// fall through to allowed.
//
// Precedence:
//   1. Legacy per-route entry (option[i].name = "allworkspace" …)
//      keeps working unchanged — wins if present.
//   2. New section entry with `items[]` — item-level role wins.
//   3. New section entry without `items[]` — section-level role applies
//      to every route in the section (default-allow behaviour).
export function canSee(option, routeKey) {
  if (!isOptionGated(option)) return true;
  const section = findSectionByRoute(routeKey);
  if (!section) return true;
  // Legacy fast-path: if option carries a direct route entry, honour it.
  const legacyName = Object.keys(LEGACY_ROUTE_KEY_MAP).find(
    (k) => LEGACY_ROUTE_KEY_MAP[k] === routeKey,
  );
  if (legacyName) {
    const legacyEntry = option.find((o) => o?.name === legacyName);
    if (legacyEntry) return legacyEntry.role === "allow";
  }
  const sectionEntry = option.find((o) => o?.key === section.key);
  if (sectionEntry) {
    // Section turned off → all items off, no need to check items[].
    if (sectionEntry.role !== "allow") return false;
    // Section on AND items[] present → respect per-item override.
    if (Array.isArray(sectionEntry.items) && sectionEntry.items.length) {
      const itemEntry = sectionEntry.items.find((it) => it?.to === routeKey);
      if (itemEntry) return itemEntry.role === "allow";
    }
    // Section on, no items[] → default allow for everything in this
    // section (preserves existing members' access).
    return true;
  }
  return hasSection(option, section.key);
}

// First section the user is allowed into → its landing route.
// Used to redirect post-login or after invite-accept so we never land
// a member on a page they can't see.
export function firstAllowedLanding(option) {
  if (!isOptionGated(option)) return "/dashboard/myapps";
  for (const s of SECTIONS) {
    if (hasSection(option, s.key)) return `/dashboard/${s.landing}`;
  }
  return "/dashboard/myapps";
}

export function allowedSections(option) {
  if (!isOptionGated(option)) return SECTIONS;
  return SECTIONS.filter((s) => hasSection(option, s.key));
}

// Default value the InviteDialog seeds the form with. Per user
// direction (2026-06-15): seed sections as "noallow" with items also
// "noallow" so the inviter must explicitly grant access — they pick
// the section, then pick the specific items inside it. Existing
// members who already had `{key, role}` entries without items[] still
// work (canSee falls back to section-level role).
export function defaultInviteOption() {
  return SECTIONS.map((s) => ({
    key: s.key,
    Title: s.title,
    role: "noallow",
    items: s.routes.map((r) => ({
      to: r,
      label: ROUTE_LABELS[r] || r,
      role: "noallow",
    })),
  }));
}

// Project ANY stored option array onto the new section-based shape so
// the EditInviteDialog renders the same five-toggle UI regardless of
// when the original invite was issued. New-shape entries pass through;
// legacy per-route entries (allworkspace, alluser, …) are collapsed
// to their containing section — section.role = "allow" iff ANY legacy
// child route was allowed.
export function upgradeStoredOption(stored) {
  if (!Array.isArray(stored)) return defaultInviteOption();
  return SECTIONS.map((s) => {
    const existing = stored.find((o) => o?.key === s.key);
    if (existing) {
      // Section-level role preserved as-is.
      const sectionRole = existing.role === "allow" ? "allow" : "noallow";
      // Carry over existing items[] when present so the dialog reflects
      // whatever per-item grants the user already had. Missing items
      // inherit the section role.
      const storedItems = Array.isArray(existing.items) ? existing.items : [];
      const items = s.routes.map((r) => {
        const it = storedItems.find((x) => x?.to === r);
        if (it) {
          return {
            to: r,
            label: ROUTE_LABELS[r] || r,
            role: it.role === "allow" ? "allow" : "noallow",
          };
        }
        // No explicit item entry — inherit section role (default-allow
        // when section is on, default-noallow when section is off).
        return {
          to: r,
          label: ROUTE_LABELS[r] || r,
          role: sectionRole,
        };
      });
      return {
        key: s.key,
        Title: s.title,
        role: sectionRole,
        items,
      };
    }
    // Legacy per-route entries (allworkspace, alluser, …) — project
    // each legacy grant onto its containing item, and set the section
    // role to "allow" iff any child route was allowed.
    const legacyItems = s.routes.map((r) => {
      const legacyName = Object.keys(LEGACY_ROUTE_KEY_MAP).find(
        (k) => LEGACY_ROUTE_KEY_MAP[k] === r,
      );
      if (!legacyName) {
        return { to: r, label: ROUTE_LABELS[r] || r, role: "noallow" };
      }
      const e = stored.find((o) => o?.name === legacyName);
      return {
        to: r,
        label: ROUTE_LABELS[r] || r,
        role: e && e.role === "allow" ? "allow" : "noallow",
      };
    });
    const anyAllowed = legacyItems.some((it) => it.role === "allow");
    return {
      key: s.key,
      Title: s.title,
      role: anyAllowed ? "allow" : "noallow",
      items: legacyItems,
    };
  });
}
